import React, { useState, useEffect } from "react";
import { StyleSheet, View, TouchableOpacity, Image, Platform } from "react-native";
import { ThemedText } from "../ThemedText";
import { ContactModel, ShareModel, Contact, GroupModel, ICustomCardEntityConfig, formatCardTimestamp } from "common247";
import { Ionicons } from "@expo/vector-icons";
import { ShareSelector } from "../sharing/ShareSelector";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ButtonPaddings } from "@/constants/themes/components";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { ThemedListViewMode } from "../ThemedList";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedChip } from "../ThemedChip";
import { ThemedRating } from "../ThemedRating";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedMediumCard } from "../cards/ThemedMediumCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import {
  resolveCardLinkedContacts,
  resolveCardLinkedGroups,
  resolveCardLinkedMedia,
  resolveCardLinkedMemories,
  resolveCardLinkedPlaces,
  resolveCardLinkedAnchors,
} from "../cards/card-helpers";
import TagListStripDefault, { TagListStrip as TagListStripNamed } from "@/components/tags/TagListStrip";
const TagListStrip = TagListStripNamed || TagListStripDefault;

/**
 * Props for the ContactCard component.
 */
export interface IContactCardProps {
  contact: ContactModel;
  isMe?: boolean;
  isAdminView?: boolean;
  /** Whether the user is allowed to add or remove tags (explicit override) */
  canEditTags?: boolean;
  /** Current authenticated user ID for tag removal authorization */
  currentUserId?: string;
  compact?: boolean;
  viewMode?: ThemedListViewMode;
  /** Whether this card is currently hovered in the parent list */
  isHovered?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onPress?: () => void;
  onLongPress?: () => void;
  onContextMenu?: (e?: any) => void;
  onEditPress?: () => void;
  onInspect?: () => void;
  onShare?: () => void;
  onDelete?: () => void;
  onInvite?: () => void;

  /** Existing shares associated with this contact */
  shares?: ShareModel[];
  /** Available contacts to select from when adding a new share */
  availableContacts?: (Contact | ContactModel)[];
  /** Available groups to select from when adding a new share */
  availableGroups?: GroupModel[];
  /** Available places to resolve linked place metadata */
  availablePlaces?: any[];
  /** Available anchors to resolve linked anchor metadata */
  availableAnchors?: any[];
  /** Loading flag while fetching shares */
  isLoadingShares?: boolean;
  /** Callback when a new share is created or role updated */
  onShareTarget?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Callback when a share is revoked */
  onUnshare?: (shareId: string) => void;
  /** Controlled expanded state for sharing control */
  isSharingExpanded?: boolean;
  /** Callback to toggle expanded sharing control */
  onToggleSharing?: () => void;
  /** Optional custom card configuration */
  config?: ICustomCardEntityConfig;
}

/**
 * Card component displaying contact details with embedded sharing management.
 */
export const ContactCard: React.FC<IContactCardProps> = ({
  contact,
  isMe,
  isAdminView = false,
  canEditTags,
  currentUserId,
  compact = false,
  viewMode,
  isHovered,
  selectionMode = false,
  isSelected = false,
  onPress,
  onLongPress,
  onContextMenu,
  onEditPress,
  onInspect,
  onShare,
  onDelete,
  onInvite,
  shares,
  availableContacts,
  availableGroups,
  availablePlaces,
  availableAnchors,
  isLoadingShares,
  onShareTarget,
  onUnshare,
  isSharingExpanded,
  onToggleSharing,
  config,
}) => {
  const userRole = isAdminView ? "viewer" : (contact.userRole || "owner");
  const isOwner = isAdminView ? false : (userRole === "owner");
  const isViewer = isAdminView ? true : (userRole === "viewer");
  const canEdit = !isAdminView && (isOwner || userRole === "editor");
  const effectiveCanEditTags = canEditTags !== undefined ? canEditTags : (!isAdminView && canEdit);
  const effectiveIsOwnerForTags = (canEditTags === false || isAdminView) ? false : isOwner;
  const roleName = isOwner ? undefined : isViewer ? "Shared Contact (View Only)" : "Shared Contact";

  // Identify whether this contact represents the logged-on AppUser / self contact
  const isSelf = Boolean(
    isMe ||
    (currentUserId && contact.userId && contact.userId === currentUserId && contact.ownerId === currentUserId)
  );

  // Compare AppUser linked name vs Contact displayName across all Contact Cards:
  // Only display AppUser name if it differs from the contact's displayName
  const hasDifferentLinkedName = Boolean(
    contact.userId &&
    contact.userDisplayName &&
    contact.userDisplayName.trim().toLowerCase() !== (contact.displayName || "").trim().toLowerCase()
  );
  const linkedNameSuffix = hasDifferentLinkedName ? ` (${contact.userDisplayName})` : "";

  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isSharingExpanded !== undefined ? isSharingExpanded : localExpanded;

  const [lastKnownShares, setLastKnownShares] = useState<ShareModel[] | null>(null);

  useEffect(() => {
    if (shares !== undefined) {
      setLastKnownShares(shares);
    }
  }, [shares]);

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const tintColor = useThemeColor({}, "tint");
  const activeBorderColor = isSelected ? tintColor : isHovered ? hoverBorderColor : borderColor;
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const buttonColor = useThemeColor({}, "button");
  const textMuted = useThemeColor({ light: "#64748B", dark: "#94A3B8" }, "text");

  const handleSharePress = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (onToggleSharing) {
      onToggleSharing();
    } else if (onShareTarget || shares !== undefined) {
      setLocalExpanded(!localExpanded);
    } else if (onShare) {
      onShare();
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  const effectiveShares: ShareModel[] =
    shares ??
    lastKnownShares ??
    (contact.sharedWith?.map((s) => ({
      id: s.id,
      sourceId: contact.id || "",
      sourceType: "contact" as const,
      targetId: s.id,
      targetType: s.type,
      role: s.role,
      targetDisplayName: s.displayName,
      targetUserId: s.targetUserId,
      sharedBy: contact.ownerId || "",
      sharedAt: contact.createdAt,
    })) as ShareModel[]) ??
    [];

  const canShowShareButton = contact.canShare && (onShare || onShareTarget || onToggleSharing || shares !== undefined);

  const kebabActions: IKebabActionItem[] = [];
  if (canShowShareButton) {
    kebabActions.push({
      id: "share",
      label: isExpanded ? "Close Sharing" : "Manage Sharing",
      icon: "share-social-outline",
      onPress: handleSharePress,
    });
  }
  if (canEdit && onEditPress) {
    kebabActions.push({
      id: "edit",
      label: "Edit Contact",
      icon: "pencil-outline",
      onPress: onEditPress,
    });
  }
  if (isOwner && onInvite && !contact.userId && contact.email?.trim()) {
    kebabActions.push({
      id: "invite",
      label: "Invite to Platform",
      icon: "mail-outline",
      onPress: onInvite,
    });
  }
  // The AppUser self-contact cannot EVER be unlinked or deleted
  if (isOwner && onDelete && !isSelf) {
    kebabActions.push({
      id: "delete",
      label: "Delete Contact",
      icon: "trash-outline",
      destructive: true,
      onPress: onDelete,
    });
  }

  if (viewMode === "chip") {
    return (
      <View style={{ margin: 2 }}>
        <ThemedChip
          entityType="contact"
          id={contact.id}
          label={contact.displayName || "Contact"}
          thumbnailUrl={contact.photoIconUrl || contact.photoUrl}
          isOwner={isOwner}
          isSelf={isSelf}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      </View>
    );
  }

  const resolvedMemories = resolveCardLinkedMemories(contact);
  const resolvedMedia = resolveCardLinkedMedia(contact);
  const resolvedGroups = resolveCardLinkedGroups(contact, availableGroups);
  const resolvedContacts = resolveCardLinkedContacts(contact, availableContacts);
  const resolvedPlaces = resolveCardLinkedPlaces(contact, availablePlaces);
  const resolvedAnchors = resolveCardLinkedAnchors(contact, availableAnchors);

  if (viewMode === "medium") {
    const formattedDate = contact.createdAt
      ? `Created: ${formatCardTimestamp(contact.createdAt)}`
      : undefined;

    return (
      <ThemedMediumCard
        entityType="contact"
        id={contact.id}
        title={`${contact.displayName || "Contact"}${linkedNameSuffix}`}
        formattedDate={formattedDate}
        description={contact.description}
        email={contact.email}
        thumbnailUrl={contact.photoIconUrl || contact.photoUrl}
        ownerDisplayName={!isOwner ? (contact.ownerDisplayName || "Unknown") : undefined}
        isOwner={isOwner}
        isSelf={isSelf}
        isFiveStar={contact.favouriteRating === 5}
        kebabActions={kebabActions}
        mediaList={resolvedMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        placesList={resolvedPlaces}
        anchorsList={resolvedAnchors}
        tagsList={contact.tags}
        sharesList={effectiveShares}
        createdAt={contact.createdAt}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={!isSelf ? onDelete : undefined}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "full") {
    const formattedDate = contact.createdAt
      ? `Created: ${formatCardTimestamp(contact.createdAt)}`
      : undefined;

    return (
      <ThemedFullCard
        entityType="contact"
        id={contact.id}
        title={`${contact.displayName || "Contact"}${linkedNameSuffix}`}
        formattedDate={formattedDate}
        description={contact.description}
        email={contact.email}
        thumbnailUrl={contact.photoIconUrl || contact.photoUrl}
        ownerDisplayName={!isOwner ? (contact.ownerDisplayName || "Unknown") : undefined}
        isOwner={isOwner}
        isSelf={isSelf}
        isFiveStar={contact.favouriteRating === 5}
        kebabActions={kebabActions}
        mediaList={resolvedMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        placesList={resolvedPlaces}
        anchorsList={resolvedAnchors}
        tagsList={contact.tags}
        sharesList={effectiveShares}
        createdAt={contact.createdAt}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={!isSelf ? onDelete : undefined}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "custom") {
    return (
      <ThemedCustomCard
        entityType="contact"
        id={contact.id}
        item={contact}
        title={`${contact.displayName || "Contact"}${linkedNameSuffix}`}
        email={contact.email}
        thumbnailUrl={contact.photoIconUrl || contact.photoUrl}
        isOwner={isOwner}
        isSelf={isSelf}
        kebabActions={kebabActions}
        mediaList={resolvedMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        placesList={resolvedPlaces}
        tagsList={contact.tags}
        sharesList={effectiveShares}
        config={config}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={!isSelf ? onDelete : undefined}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "details") {
    return (
      <View
        style={[
          styles.detailsRowContainer,
          { backgroundColor: cardBgColor, borderColor: activeBorderColor },
          !contact.isActive && styles.cardInactive,
        ]}
      >
        <TouchableOpacity
          style={styles.detailsRowMain}
          onPress={onPress}
          onLongPress={onLongPress}
          delayLongPress={350}
          activeOpacity={0.7}
          {...(Platform.OS === "web" && onContextMenu ? {
            onContextMenu: (e: any) => {
              e.preventDefault();
              onContextMenu(e);
            },
          } : {})}
        >
          {selectionMode || isSelected ? (
            <View style={[styles.detailsCheckbox, isSelected && { backgroundColor: tintColor, borderColor: tintColor }]}>
              {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
            </View>
          ) : null}
          <View style={styles.detailsAvatarContainer}>
            {contact.photoIconUrl || contact.photoUrl ? (
              <Image
                source={{ uri: contact.photoIconUrl || contact.photoUrl }}
                style={styles.detailsAvatarImage}
              />
            ) : (
              <View style={[styles.detailsAvatarPlaceholder, { backgroundColor: tintColor }]}>
                <ThemedText style={styles.detailsAvatarInitial}>
                  {(contact.displayName || "?")[0].toUpperCase()}
                </ThemedText>
              </View>
            )}
          </View>

          <View style={styles.detailsInfo}>
            <View style={styles.detailsNameRow}>
              {isSelf ? (
                <ThemedPlatformSymbol type="you" size="small" />
              ) : null}
              {isOwner ? (
                <ThemedPlatformSymbol type="owner" size="small" />
              ) : (
                <ThemedPlatformSymbol type="shared" size="small" />
              )}
              <ThemedText type="defaultSemiBold" style={styles.detailsDisplayName} numberOfLines={1}>
                {contact.displayName || "No Name"}
              </ThemedText>
              {hasDifferentLinkedName ? (
                <ThemedText style={styles.detailsLinkedUserText} numberOfLines={1}>
                  ({contact.userDisplayName})
                </ThemedText>
              ) : null}
              {contact.userId ? <Ionicons name="checkmark-circle" size={13} color="#1e8e3e" /> : null}
              {!isOwner && roleName ? (
                <View style={[styles.inlineRoleBadge, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.gridRoleBadgeText}>{roleName}</ThemedText>
                </View>
              ) : null}
            </View>

            <View style={styles.detailsSubRow}>
              <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                {contact.email || "No email"}
              </ThemedText>
              {!contact.isActive ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • Inactive
                </ThemedText>
              ) : null}
              {(contact as any).phone || (contact as any).phoneNumber ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • {(contact as any).phone || (contact as any).phoneNumber}
                </ThemedText>
              ) : null}
              {!isOwner && contact.ownerDisplayName ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • Shared by: {contact.ownerDisplayName}
                </ThemedText>
              ) : null}
            </View>
            <TagListStrip
              entityId={contact.id!}
              entityType="contact"
              tags={contact.tags}
              canEdit={effectiveCanEditTags}
              isOwner={effectiveIsOwnerForTags}
              currentUserId={currentUserId}
              compact={true}
            />
            {effectiveShares.length > 0 && (
              <TouchableOpacity
                onPress={handleSharePress}
                style={{ marginTop: 2, alignSelf: "flex-start" }}
                activeOpacity={0.7}
                testID={`contact-share-pill-details-${contact.id || "card"}`}
              >
                <ThemedText style={[styles.detailsMetaText, { color: tintColor, fontWeight: "600" }]} numberOfLines={1}>
                  👥 Shared ({effectiveShares.length})
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>

          {kebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={kebabActions}
              compact={true}
              accessibilityLabel={`Options for ${contact.displayName || "contact"}`}
            />
          )}
        </TouchableOpacity>

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={contact.displayName || contact.name}
          itemType="contact"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={contact.canShare}
          canUnshare={contact.canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`contact-share-picker-details-${contact.id || "card"}`}
        />
      </View>
    );
  }

  if (viewMode === "compact") {
    const compactTitle = `${contact.displayName || "Contact"}${linkedNameSuffix}`;
    return (
      <View style={{ position: "relative", width: "100%" }}>
        <ThemedCompactCard
          entityType="contact"
          id={contact.id}
          title={compactTitle}
          thumbnailUrl={contact.photoIconUrl || contact.photoUrl}
          isOwner={isOwner}
          isSelf={isSelf}
          selectionMode={selectionMode}
          isSelected={isSelected}
          onSelect={onPress}
          onPress={onPress}
          onLongPress={onLongPress}
          onContextMenu={onContextMenu}
          kebabActions={kebabActions}
          testID={`contact-compact-card-${contact.id || "card"}`}
        />

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={contact.displayName || contact.name}
          itemType="contact"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={contact.canShare}
          canUnshare={contact.canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`contact-share-picker-compact-${contact.id || "card"}`}
        />
      </View>
    );
  }

  const isSmall = viewMode === "small" || compact;

  return (
    <TouchableOpacity
      style={[
        styles.card,
        isSmall && styles.cardCompact,
        { backgroundColor: cardBgColor, borderColor: activeBorderColor },
        !contact.isActive && styles.cardInactive,
      ]}
      onPress={onPress}
      onLongPress={onLongPress}
      delayLongPress={350}
      activeOpacity={0.7}
      {...(Platform.OS === "web" && onContextMenu ? {
        onContextMenu: (e: any) => {
          e.preventDefault();
          onContextMenu(e);
        },
      } : {})}
    >
      <View style={styles.cardHeader}>
        {selectionMode && (
          <TouchableOpacity
            style={[
              styles.detailsCheckbox,
              isSelected && { backgroundColor: tintColor, borderColor: tintColor },
            ]}
            onPress={(e) => {
              e.stopPropagation?.();
              onPress?.();
            }}
          >
            {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
          </TouchableOpacity>
        )}
        <View style={styles.contactAvatarContainer}>
          {contact.photoIconUrl || contact.photoUrl ? (
            <Image
              source={{ uri: contact.photoIconUrl || contact.photoUrl }}
              style={styles.contactAvatarImage}
            />
          ) : (
            <View style={[styles.contactAvatarPlaceholder, { backgroundColor: tintColor }]}>
              <ThemedText style={styles.contactAvatarInitial}>
                {(contact.displayName || "?")[0].toUpperCase()}
              </ThemedText>
            </View>
          )}
        </View>

        <View style={styles.titleArea}>
          <View style={styles.titleRow}>
            <View style={styles.titleWithStatus}>
              <ThemedText type="defaultSemiBold" style={[styles.cardTitle, compact && styles.cardTitleCompact]}>
                {contact.displayName || "No Name"}
              </ThemedText>
              {contact.favouriteRating ? (
                <View style={{ marginLeft: 6 }}>
                  <ThemedRating
                    value={contact.favouriteRating}
                    size={12}
                    showLabel={false}
                    readOnly={true}
                  />
                </View>
              ) : null}
              {hasDifferentLinkedName ? (
                <ThemedText style={styles.cardLinkedUserText} numberOfLines={1}>
                  ({contact.userDisplayName})
                </ThemedText>
              ) : null}
              {!contact.isActive ? (
                <ThemedText
                  type="stamp"
                  style={[styles.statusBadge, styles.statusInactive]}
                >
                  Inactive
                </ThemedText>
              ) : null}
            </View>
          </View>

          <View style={styles.roleBadgeRow}>
            {!isOwner && roleName ? (
              <View style={[styles.roleBadge, { backgroundColor: badgeBg }]}>
                <ThemedText type="stamp" style={styles.roleBadgeText}>{roleName}</ThemedText>
              </View>
            ) : null}
            {!isOwner && <ThemedText style={[styles.ownerText, { color: tintColor }]}>• Shared by: {contact.ownerDisplayName || "Unknown"}</ThemedText>}
            {contact.userId ? (
              <View style={styles.linkedBadge}>
                <Ionicons name="checkmark-circle" size={12} color="#1e8e3e" />
                <ThemedText style={styles.linkedBadgeText}>Linked</ThemedText>
              </View>
            ) : null}
          </View>
          {isAdminView ? <ThemedText style={styles.idText}>ID: {contact.id}</ThemedText> : null}
        </View>

        <View style={styles.cardTopRightGroup}>
          <View style={styles.symbolsGroup}>
            {isSelf ? (
              <ThemedPlatformSymbol type="you" size="small" />
            ) : null}
            {isOwner ? (
              <ThemedPlatformSymbol type="owner" size="small" />
            ) : (
              <ThemedPlatformSymbol type="shared" size="small" />
            )}
          </View>
          {kebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={kebabActions}
              compact={compact}
              accessibilityLabel={`Options for ${contact.displayName || "contact"}`}
            />
          )}
        </View>
      </View>
      <ThemedText style={[styles.cardEmail, !contact.email && styles.cardEmailEmpty]}>
        {contact.email || "No email address"}
      </ThemedText>
      {contact.description ? (
        <ThemedText style={[styles.cardEmail, { fontStyle: "italic", opacity: 0.85, marginTop: 2 }]}>
          {contact.description}
        </ThemedText>
      ) : null}

      {isAdminView ? (
        <View style={[styles.detailsBox, { backgroundColor: badgeBg }]}>
          <ThemedText style={styles.dateText}>Joined: {formatCardTimestamp(contact.createdAt)}</ThemedText>
          {contact.userId ? (
            <ThemedText style={styles.linkedText}>
              Linked User: {contact.userDisplayName ? `${contact.userDisplayName} (${contact.userId})` : contact.userId}
            </ThemedText>
          ) : null}
        </View>
      ) : (
        <View style={[styles.cardFooter, { borderTopColor: borderColor }]}>
          <ThemedText style={styles.dateText}>Created: {formatCardTimestamp(contact.createdAt)}</ThemedText>
          {!contact.userId && !!contact.email && onInvite ? (
            <TouchableOpacity
              style={[styles.inviteBtn, { borderColor: buttonColor + "30" }]}
              onPress={(e) => {
                e.stopPropagation();
                onInvite();
              }}
            >
              <ThemedText style={[styles.inviteBtnText, { color: buttonColor }]}>✉️ Invite to Platform</ThemedText>
            </TouchableOpacity>
          ) : null}
        </View>
      )}

      {isAdminView && (
        <View style={styles.adminActions}>
          <TouchableOpacity style={styles.inspectBtn} onPress={onInspect}>
            <ThemedText style={styles.inspectBtnText}>Inspect</ThemedText>
          </TouchableOpacity>
        </View>
      )}

      {/* Contact & Groups Section (Single Section Standard) */}
      {contact.sharedTo && contact.sharedTo.length > 0 && (
        <View style={[styles.groupsSection, { borderTopColor: borderColor }]}>
          <View style={styles.contactAndGroupsHeaderRow}>
            <Ionicons name="people-outline" size={11} color={tintColor} />
            <ThemedText type="stamp" style={[styles.contactAndGroupsStamp, { color: textMuted }]}>
              CONTACT &amp; GROUPS ({contact.sharedTo.length})
            </ThemedText>
          </View>
          <View style={styles.chipRow}>
            {contact.sharedTo.map((target, idx) => (
              <ThemedChip
                key={`contact-used-group-${target.id || idx}`}
                entityType="group"
                id={target.id}
                label={target.displayName || target.name || "Group"}
                compact={isSmall}
              />
            ))}
          </View>
        </View>
      )}

      <TagListStrip
        entityId={contact.id!}
        entityType="contact"
        tags={contact.tags}
        canEdit={effectiveCanEditTags}
        isOwner={effectiveIsOwnerForTags}
        currentUserId={currentUserId}
        compact={compact}
      />

      {/* Shares Section (Proposed 5-Tier Item Card Standard) */}
      {effectiveShares.length > 0 && (
        <View style={[styles.traySection, { borderTopColor: borderColor }]}>
          <View style={styles.trayHeaderRow}>
            <Ionicons name="share-social-outline" size={11} color={tintColor} />
            <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
              SHARED WITH ({effectiveShares.length})
            </ThemedText>
          </View>
          <View style={styles.chipsWrap}>
            {effectiveShares.map((s, idx) => (
              <ThemedChip
                key={`contact-share-${s.id || idx}`}
                entityType="share"
                id={s.id}
                label={contact.displayName || "Contact"}
                secondaryLabel={s.targetDisplayName || s.targetId || "Recipient"}
                compact={isSmall}
                onPress={handleSharePress}
              />
            ))}
          </View>
          <TouchableOpacity
            style={[styles.compactSharePill, { display: "none" }]}
            onPress={contact.canShare ? handleSharePress : undefined}
            activeOpacity={contact.canShare ? 0.7 : 1}
            accessibilityLabel={`Shared with ${effectiveShares.length} recipients. Tap to manage sharing.`}
            testID={`contact-share-pill-${contact.id || "card"}`}
          >
            <ThemedText style={[styles.compactShareText, { color: tintColor }]}>
              Shared ({effectiveShares.length})
            </ThemedText>
          </TouchableOpacity>
        </View>
      )}

      <ShareSelector
        visible={isExpanded}
        onClose={handleSharePress}
        itemTitle={contact.displayName || contact.name}
        itemType="contact"
        shares={effectiveShares}
        contacts={availableContacts || []}
        groups={availableGroups || []}
        isLoading={isLoadingShares}
        canShare={contact.canShare}
        canUnshare={contact.canShare}
        onShare={onShareTarget || (() => {})}
        onUnshare={onUnshare || (() => {})}
        testID={`contact-share-picker-${contact.id || "card"}`}
      />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    padding: 12,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#ddd",
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    marginBottom: 6,
    gap: 8,
  },
  cardCompact: {
    padding: 6,
    borderRadius: 4,
    marginBottom: 4,
  },
  cardInactive: {
    opacity: 0.8,
    borderColor: "#fcc",
    backgroundColor: "rgba(200, 0, 0, 0.02)",
    borderRadius: 4,
  },
  cardTopRightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    zIndex: 10,
  },
  symbolsGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 2,
  },
  contactAvatarContainer: {
    width: 38,
    height: 38,
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 1,
    flexShrink: 0,
  },
  contactAvatarImage: {
    width: 38,
    height: 38,
    borderRadius: 4,
  },
  contactAvatarPlaceholder: {
    width: 38,
    height: 38,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  contactAvatarInitial: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "bold",
  },
  traySection: {
    borderTopWidth: 1,
    paddingTop: 6,
    gap: 4,
    marginTop: 4,
  },
  trayHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  trayTitle: {
    fontSize: 9,
    letterSpacing: 0.8,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 2,
  },
  titleArea: {
    flex: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
    marginBottom: 2,
  },
  titleWithStatus: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    flexWrap: "wrap",
    gap: 4,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
  },
  cardTitleCompact: {
    fontSize: 11,
    fontWeight: "600",
  },
  cardLinkedUserText: {
    fontSize: 11,
    fontWeight: "500",
    opacity: 0.7,
    fontStyle: "italic",
  },
  roleBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 3,
  },
  linkedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#e6f4ea",
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  linkedBadgeText: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: "#1e8e3e",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  roleBadge: {
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  viewerBadge: {
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  editorBadge: {
    backgroundColor: "rgba(74, 144, 226, 0.1)",
  },
  roleBadgeText: {
    fontSize: 8.5,
    fontWeight: "bold",
    textTransform: "uppercase",
    opacity: 0.6,
  },
  gridActionRow: {
    flexDirection: "row",
    gap: 2,
  },
  gridActionBtn: {
    padding: 2,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  gridDeleteBtn: {
    backgroundColor: "rgba(217, 48, 37, 0.1)",
  },
  gridActionIcon: {
    fontSize: 9,
    opacity: 0.6,
  },
  idText: {
    fontSize: 8.5,
    opacity: 0.4,
    fontFamily: "monospace",
  },
  statusBadge: {
    fontSize: 9.5,
    fontWeight: "bold",
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
    overflow: "hidden",
  },
  statusActive: {
    backgroundColor: "#e6f4ea",
    color: "#1e8e3e",
  },
  statusInactive: {
    backgroundColor: "#fce8e6",
    color: "#d93025",
  },
  cardEmail: {
    fontSize: 10.5,
    marginBottom: 2,
    opacity: 0.8,
  },
  cardEmailEmpty: {
    fontStyle: "italic",
    opacity: 0.45,
  },
  detailsBox: {
    backgroundColor: "rgba(0,0,0,0.02)",
    padding: 4,
    borderRadius: 0,
    marginBottom: 4,
  },
  dateText: {
    fontSize: 9.5,
    opacity: 0.5,
  },
  linkedText: {
    fontSize: 9.5,
    color: "#1e8e3e",
    fontWeight: "bold",
    marginTop: 2,
  },
  cardFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
    paddingTop: 4,
  },
  actionRow: {
    flexDirection: "row",
    gap: 4,
  },
  shareBtn: {
    backgroundColor: "rgba(74, 144, 226, 0.1)",
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    borderRadius: 0,
  },
  shareBtnText: {
    fontSize: 10,
    color: "#4A90E2",
    fontWeight: "bold",
  },
  deleteBtn: {
    backgroundColor: "rgba(217, 48, 37, 0.1)",
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    borderRadius: 0,
  },
  deleteBtnText: {
    fontSize: 10,
    color: "#d93025",
    fontWeight: "bold",
  },
  viewBtn: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  viewBtnText: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.7,
  },
  adminActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
    paddingTop: 4,
  },
  inspectBtn: {
    paddingHorizontal: ButtonPaddings.compact.paddingHorizontal,
    paddingVertical: ButtonPaddings.compact.paddingVertical,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  inspectBtnText: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.7,
  },
  groupsSection: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  contactAndGroupsHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 4,
  },
  contactAndGroupsStamp: {
    fontSize: 9,
    letterSpacing: 0.5,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  groupsLabel: {
    fontSize: 9.5,
    fontWeight: "bold",
    opacity: 0.6,
    marginBottom: 3,
  },
  groupBadgeRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3,
  },
  groupBadge: {
    backgroundColor: "rgba(0,0,0,0.05)",
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 0,
  },
  groupBadgeText: {
    fontSize: 9,
    fontWeight: "500",
  },
  sharesSection: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  sharesLabel: {
    fontSize: 9,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  sharesList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3,
  },
  sharePillContainer: {
    marginTop: 3,
    marginBottom: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  compactSharePill: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderRadius: 0,
    alignSelf: "flex-start",
  },
  compactShareText: {
    fontSize: 11,
    fontWeight: "600",
  },
  sharesHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 3,
  },
  manageSharesText: {
    fontSize: 9.5,
    fontWeight: "600",
  },
  inlineSharingSection: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  inlineSharingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  inlineSharingTitle: {
    fontSize: 11,
    letterSpacing: -0.2,
  },
  collapseBtn: {
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 0,
  },
  collapseBtnText: {
    fontSize: 9.5,
    fontWeight: "600",
    opacity: 0.7,
  },
  inviteBtn: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 0,
    borderWidth: 1,
  },
  inviteBtnText: {
    fontSize: 10,
    fontWeight: "bold",
  },
  ownerText: {
    fontSize: 9.5,
    opacity: 0.6,
    fontStyle: "italic",
  },
  // Details view styles
  detailsRowContainer: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    marginVertical: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  detailsRowMain: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 3,
    paddingHorizontal: 6,
    gap: 6,
  },
  detailsAvatarContainer: {
    width: 28,
    height: 28,
    borderRadius: 0,
    overflow: "hidden",
  },
  detailsAvatarImage: {
    width: 28,
    height: 28,
  },
  detailsAvatarPlaceholder: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  detailsAvatarInitial: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#fff",
  },
  detailsInfo: {
    flex: 1,
    justifyContent: "center",
    gap: 1,
  },
  detailsNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  detailsDisplayName: {
    fontSize: 11,
    fontWeight: "600",
    flexShrink: 1,
  },
  detailsLinkedUserText: {
    fontSize: 10,
    fontWeight: "500",
    opacity: 0.7,
    fontStyle: "italic",
  },
  detailsSubRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexWrap: "wrap",
  },
  detailsMetaText: {
    fontSize: 9.5,
    opacity: 0.65,
  },
  inlineRoleBadge: {
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 0,
  },
  gridRoleBadgeText: {
    fontSize: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    opacity: 0.8,
  },
  // Compact view styles (14 columns)
  compactModeCard: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    paddingVertical: 4,
    paddingHorizontal: 5,
    borderRadius: 0,
    borderWidth: 1,
    minHeight: 38,
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 1,
  },
  compactModeTitle: {
    fontSize: 9.5,
    fontWeight: "600",
    lineHeight: 12,
  },
  detailsCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 0,
    borderWidth: 1.5,
    borderColor: "rgba(150, 150, 150, 0.4)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  smallSelectionBadge: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 14,
    height: 14,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 14,
  },
  selectionBadge: {
    position: "absolute",
    top: 6,
    right: 34,
    width: 20,
    height: 20,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 14,
  },
  selectionText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
  },
  cardTopRightAction: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 15,
  },
  compactTopRightAction: {
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 15,
  },
});
